import firebase from "firebase/compat/app";
import { fetchCompanies } from "../../../../services/companiesServices";
import { fetchAllUsersWeeklyHoursMap, isDateInRange } from "./utils";
import { fetchUsers } from "../../../../services/usersServices";
import { rateCardsCollection } from "../../../../services/firebase";

const escapeCSVField = (field) => {
  if (typeof field === "string") {
    // Replace newlines or carriage returns with a space
    const cleanedField = field.replace(/(\r\n|\n|\r)/gm, " ");
    // Escape commas and double quotes
    return `"${cleanedField.replace(/"/g, '""')}"`;
  }
  return field;
};

const getProjectName = (projectData) => {
  if (projectData.projectName) {
    return projectData.projectName; // Old structure
  }
  return projectData.builderData?.name || "Unknown Project"; // New structure
};

const generateCSVData = async (timesheetData, startDate, endDate) => {
  if (!timesheetData || timesheetData.length === 0) return "";

  const fields = [
    "User Name",
    "Service Group",
    "Flow ID",
    "Client",
    "Project Group Name",
    "Project Name",
    "State",
    "Deliverable",
    "Role",
    "Rate",
    "Time Entry Date",
    "Billable Hours",
    "Billable Hours Value ($)",
    "Non-Billable Hours",
    "Note",
  ];

  const usersData = await fetchUsers();
  const companies = await fetchCompanies();
  const rateCardSnap = await rateCardsCollection.get();

  // 🔁 Load and flatten all rate cards
  const rateCardMap = {};
  rateCardSnap.docs.forEach((doc) => {
    rateCardMap[String(doc.id)] = doc.data();
  });

  // 🔁 Build quick company lookup by companyID
  const companyMap = {};
  companies.forEach((c) => {
    companyMap[c.companyID] = c;
  });

  const csvData = await Promise.all(
    timesheetData
      .filter((entry) => {
        const isInRange = isDateInRange(
          entry.timeEntryDate,
          startDate,
          endDate
        );
        return parseFloat(entry.billableHours) > 0 && isInRange;
      })
      .map(async (entry) => {
        const user = usersData.find((u) => u.email === entry.userEmail);
        const name = user ? `${user.firstName} ${user.surname}` : "Unknown";
        const serviceGroup = user?.serviceGroup || "Unknown";
        const userRole = user?.role || null;

        // 🔍 Rate resolution logic with full preload
        let rate = 0;
        const projectSnap = await firebase
          .firestore()
          .collection("projects")
          .doc(entry.flowId)
          .get();

        if (projectSnap.exists) {
          const project = projectSnap.data();

          const tryFindRate = (rateCard, role) => {
            if (!rateCard) return null;
            for (const group of rateCard.serviceGroups || []) {
              const match = group.roles.find((r) => r.name === role);
              if (match) return parseFloat(match.rate);
            }
            return null;
          };

          // Step 1: Try project.resources
          const resMatch = Array.isArray(project.resources)
            ? project.resources.find((r) => r.role === entry.role && r.rate)
            : null;
          if (resMatch) {
            rate = parseFloat(resMatch.rate);
          } else {
            let resolved = false;

            // Step 2: Try project.rateOption (if exists)
            if ("rateOption" in project) {
              const rateCard = rateCardMap[String(project.rateOption)];
              const projectRate =
                tryFindRate(rateCard, entry.role) ||
                tryFindRate(rateCard, userRole);
              if (projectRate !== null) {
                rate = projectRate;
                resolved = true;
              }
            }

            // Step 3: Fallback to company rateCard
            if (!resolved) {
              const company =
                companyMap[project.client] ||
                Object.values(companyMap).find(
                  (c) => c.companyName === project.client
                );

              if (company?.rateCard) {
                const rateCard = rateCardMap[String(company.rateCard)];
                const companyRate =
                  tryFindRate(rateCard, entry.role) ||
                  tryFindRate(rateCard, userRole);
                if (companyRate !== null) {
                  rate = companyRate;
                }
              }
            }
          }
        }

        const hoursDecimal = parseFloat(entry.billableHours);
        const isNonBillable = entry.nonBillableHours !== "0.00";
        const billableHoursValue = isNonBillable ? 0 : hoursDecimal * rate;

        return [
          escapeCSVField(name),
          escapeCSVField(serviceGroup),
          entry.flowId,
          escapeCSVField(entry.client),
          escapeCSVField(entry.projectGroupName || ""),
          escapeCSVField(getProjectName(entry)),
          escapeCSVField(entry.state || ""),
          escapeCSVField(entry.deliverable),
          escapeCSVField(entry.role),
          `$${rate.toFixed(2)}`,
          entry.timeEntryDate,
          isNonBillable ? "0.0" : hoursDecimal.toFixed(2),
          isNonBillable ? "$0.0" : `$${billableHoursValue.toFixed(2)}`,
          isNonBillable ? hoursDecimal.toFixed(2) : "0.0",
          escapeCSVField(entry.note || ""),
        ].join(",");
      })
  );

  return [fields.join(","), ...csvData].join("\n");
};

export const downloadTimeSheetCsv = async (
  startDate,
  endDate,
  selectedProject
) => {
  const timesheetData = await fetchAllUsersWeeklyHoursMap(startDate, endDate);
  const companies = await fetchCompanies();

  // Map company names for better display
  const updatedTimeSheetData = timesheetData.map((entry) => {
    const companyName =
      companies.find(
        (company) =>
          company.companyID === entry.client ||
          company.companyName === entry.client
      )?.companyName ||
      entry.client ||
      "N/A";

    return { ...entry, client: companyName };
  });

  // Apply project filtering if a project is selected
  const filteredData = selectedProject
    ? updatedTimeSheetData.filter(
        (entry) => entry.flowId === selectedProject.value
      )
    : updatedTimeSheetData;

  const csv = await generateCSVData(filteredData, startDate, endDate);
  if (!csv) {
    alert("No timesheet data available to download.");
    return;
  }

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "time-tracked-by-user.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
